import { Space } from 'antd';
import { memoize } from 'lodash';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { TaskId } from 'api/models';
import { FieldOptionBase } from 'api/tasks';
import { LinkToTask } from 'components/atoms/link-to-task';
import { ListWithLink } from 'components/atoms/list-with-links';
import { Priority } from 'components/atoms/priority';
import { SingleSelectField } from 'components/organisms/tasks/fields';
import { RootState } from 'data';
import { FieldsStore } from 'stores/fields';
import { Task } from 'stores/tasks';

const priority_values = ['null', 'low', 'medium', 'high'];

const getComparator = (options: FieldOptionBase[]) => {
  const getLabel = memoize(
    (value?: number) =>
      value == null ? null : options.find((o) => o.value === value)?.label,
    (value) => value?.toString() ?? 'null',
  );

  return (a: Task, b: Task) => {
    const a_value = getLabel(a.priority as any) ?? 'null';
    const b_value = getLabel(b.priority as any) ?? 'null';
    const a_index = priority_values.indexOf(a_value);
    const b_index = priority_values.indexOf(b_value);

    return a_index === b_index ? 0 : a_index > b_index ? 1 : -1;
  };
};

export const PriorityList: FC<{ data: Task[] }> = ({ data }) => {
  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const priorityField = FieldsStore.getTaskFieldByName('priority')(
    fieldsStore,
  )[1] as SingleSelectField;
  const comparator = getComparator(priorityField?.metaData?.options ?? []);
  const tasks = data.slice().sort(comparator).reverse();

  return (
    <ListWithLink
      dataSource={tasks}
      renderItem={(item) => {
        const priority = priorityField?.metaData?.options.find(
          (x) => x.value === item.priority,
        );

        return (
          <ListWithLink.Item>
            <LinkToTask id={TaskId(item.project, item.taskId)}>
              <Space>
                <span>{item.taskName}</span>
                {priority != null ? (
                  <Priority
                    priority={{
                      color: priority.color!,
                      label: priority.label,
                    }}
                  />
                ) : null}
              </Space>
            </LinkToTask>
          </ListWithLink.Item>
        );
      }}
    />
  );
};
